import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { api, rupiah, STATUS_LABELS, fileUrl } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, PackageSearch, CircleDot, MapPin, Share2 } from "lucide-react";
import { Seo } from "@/components/Seo";

const DONE = ["selesai"];
const CANCEL = ["dibatalkan_penumpang", "dibatalkan_driver", "dibatalkan_admin", "kadaluarsa", "pengembalian_dana"];
const statusColor = (s) => (DONE.includes(s) ? "bg-emerald-500/15 text-emerald-400" : CANCEL.includes(s) ? "bg-red-500/15 text-red-400" : "bg-primary/15 text-primary");

const OrderCard = ({ o, withTimeline }) => (
  <div className="border border-border bg-card p-5" data-testid={`track-order-${o.order_number}`}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="font-mono-terminal text-lg font-bold text-primary">{o.order_number}</span>
      <Badge className={`${statusColor(o.status)} hover:${statusColor(o.status)}`}>{STATUS_LABELS[o.status] || o.status}</Badge>
    </div>
    <div className="mt-3 grid gap-1 font-mono-terminal text-sm">
      <div className="flex justify-between"><span className="text-muted-foreground">Rute</span><span>{o.origin} → {o.destination}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">Jadwal</span><span>{o.date} · {o.time}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">Armada</span><span>{o.vehicle_name}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">Pax / Total</span><span>{o.pax} · {rupiah(o.total)}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">Pembayaran</span><span>{o.payment_status || "-"}</span></div>
    </div>
    {withTimeline && o.status_history?.length > 0 && (
      <div className="mt-4 border-t border-border pt-3">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Riwayat Status</div>
        <ol className="space-y-2">
          {[...o.status_history].reverse().map((h, i) => (
            <li key={i} className="flex gap-2 text-sm">
              <CircleDot className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${i === 0 ? "text-primary" : "text-muted-foreground"}`} />
              <div>
                <div className="font-medium">{STATUS_LABELS[h.status] || h.status}</div>
                <div className="font-mono-terminal text-[10px] text-muted-foreground">{new Date(h.at).toLocaleString("id-ID")} · oleh {h.by}{h.reason ? ` · ${h.reason}` : ""}</div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    )}
    <div className="mt-4 flex gap-2 text-sm">
      {o.status === "selesai" && <Link to={`/ulasan/${o.order_number}`} className="text-primary hover:underline">Beri Ulasan</Link>}
      <Link to={`/pengaduan/${o.order_number}`} className="text-primary hover:underline">Ajukan Pengaduan</Link>
    </div>
  </div>
);

const PKG_LABELS = { paket_baru: "Paket Baru", dijemput: "Dijemput", dalam_pengiriman: "Dalam Pengiriman", terkirim: "Terkirim", dibatalkan: "Dibatalkan" };
const PKG_DONE = ["terkirim"];
const PKG_CANCEL = ["dibatalkan"];
const pkgColor = (s) => (PKG_DONE.includes(s) ? "bg-emerald-500/15 text-emerald-400" : PKG_CANCEL.includes(s) ? "bg-red-500/15 text-red-400" : "bg-primary/15 text-primary");

const shareTracking = (p) => {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const url = `${origin}/lacak-order/${p.package_number}`;
  const last = (p.checkpoints || [])[p.checkpoints.length - 1];
  const lokasi = last ? `\n📍 Lokasi terakhir: ${last.city}` : "";
  const msg =
    `📦 *Lacak Paket Kilat Travele*\n` +
    `No. Kiriman: ${p.package_number}\n` +
    `${p.origin} → ${p.destination}\n` +
    `Status: ${PKG_LABELS[p.status] || p.status}${lokasi}\n\n` +
    `Pantau status & lokasi paket di sini:\n${url}`;
  window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
};

const PackageCard = ({ p }) => (
  <div className="border border-border bg-card p-5" data-testid={`track-package-${p.package_number}`}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="font-mono-terminal text-lg font-bold text-primary">{p.package_number}</span>
      <Badge className={`${pkgColor(p.status)} hover:${pkgColor(p.status)}`}>{PKG_LABELS[p.status] || p.status}</Badge>
    </div>
    <div className="mt-3 grid gap-1 font-mono-terminal text-sm">
      <div className="flex justify-between"><span className="text-muted-foreground">Rute</span><span>{p.origin} → {p.destination}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">Jenis</span><span>{p.item_type === "dokumen" ? "Dokumen" : "Barang"}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">Pengirim → Penerima</span><span>{p.sender_name} → {p.receiver_name}</span></div>
      {p.assigned_driver_name && <div className="flex justify-between"><span className="text-muted-foreground">Driver Pembawa</span><span>{p.assigned_driver_name}</span></div>}
    </div>

    <button
      type="button"
      data-testid="share-tracking-btn"
      onClick={() => shareTracking(p)}
      className="mt-3 flex w-full items-center justify-center gap-2 rounded-md bg-[#25D366] py-2.5 text-sm font-bold text-black transition-transform hover:-translate-y-0.5"
    >
      <Share2 className="h-4 w-4" /> Bagikan Tracking ke WhatsApp
    </button>

    {(p.checkpoints || []).length > 0 ? (
      <div className="mt-4 border-t border-border pt-3">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Lokasi &amp; Riwayat Kiriman</div>
        <ol className="space-y-3">
          {[...p.checkpoints].reverse().map((c, i) => (
            <li key={i} className="flex gap-2 text-sm">
              <MapPin className={`mt-0.5 h-4 w-4 shrink-0 ${i === 0 ? "text-primary" : "text-muted-foreground"}`} />
              <div className="flex-1">
                <div className="font-medium">{c.city} <span className="text-xs font-normal text-muted-foreground">· {PKG_LABELS[c.status] || c.status}</span></div>
                {c.note && <div className="text-xs text-muted-foreground">{c.note}</div>}
                <div className="font-mono-terminal text-[10px] text-muted-foreground">{new Date(c.at).toLocaleString("id-ID")}</div>
                {c.photo && <img src={fileUrl(c.photo)} alt="Foto paket" loading="lazy" className="mt-1 h-28 rounded border border-border object-cover" />}
              </div>
            </li>
          ))}
        </ol>
      </div>
    ) : (
      <p className="mt-4 border-t border-border pt-3 text-sm text-muted-foreground">Belum ada pembaruan lokasi. Driver akan memperbarui lokasi saat paket dijemput.</p>
    )}

    {(p.pickup_photo || p.delivery_photo) && (
      <div className="mt-4 grid grid-cols-2 gap-3">
        {p.pickup_photo && <div><div className="mb-1 text-xs text-muted-foreground">Foto saat dijemput</div><img src={fileUrl(p.pickup_photo)} alt="Foto dijemput" loading="lazy" className="h-28 w-full rounded border border-border object-cover" data-testid="pkg-pickup-photo" /></div>}
        {p.delivery_photo && <div><div className="mb-1 text-xs text-muted-foreground">Foto saat diterima customer</div><img src={fileUrl(p.delivery_photo)} alt="Foto diterima" loading="lazy" className="h-28 w-full rounded border border-border object-cover" data-testid="pkg-delivery-photo" /></div>}
      </div>
    )}
  </div>
);

export default function TrackOrder() {
  const { orderNumber } = useParams();
  const [input, setInput] = useState(orderNumber || "");
  const [pkg, setPkg] = useState(null);
  const [order, setOrder] = useState(null);
  const [list, setList] = useState(null);
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    const v = input.trim();
    if (!v) return;
    setLoading(true); setChecked(false); setOrder(null); setList(null); setPkg(null);
    try {
      if (/^pkt/i.test(v)) {
        const { data } = await api.get(`/packages/track/${v.toUpperCase()}`);
        setPkg(data);
      } else if (/^trv/i.test(v)) {
        const { data } = await api.get(`/orders/${v.toUpperCase()}`);
        setOrder(data);
      } else {
        const { data } = await api.get(`/orders/track/by-phone?phone=${encodeURIComponent(v)}`);
        setList(data);
      }
    } catch (e) { toast.error("Data tidak ditemukan"); }
    finally { setChecked(true); setLoading(false); }
  };

  useEffect(() => {
    if (orderNumber) search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber]);

  return (
    <div className="min-h-screen bg-background">
      <Seo
        path="/lacak-order"
        title="Lacak Order — Cek Status & Riwayat Perjalanan | Travele"
        description="Cek status dan riwayat order travel Travele menggunakan nomor order atau nomor WhatsApp Anda."
        noindex
      />
      <Navbar />
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6">
        <p className="flex items-center gap-2 font-mono-terminal text-xs uppercase tracking-widest text-primary"><PackageSearch className="h-3.5 w-3.5" /> Lacak Order</p>
        <h1 className="mt-2 font-display text-3xl font-black uppercase tracking-tighter sm:text-4xl">Cek Status & Riwayat Order</h1>
        <p className="mt-2 text-muted-foreground">Masukkan <b className="text-foreground">nomor order</b> (mis. TRV-000001) atau <b className="text-foreground">nomor kiriman paket</b> (mis. PKT-000001) untuk cek status &amp; lokasi, atau <b className="text-foreground">nomor WhatsApp</b> Anda untuk melihat riwayat order.</p>

        <div className="mt-6 flex gap-2">
          <Input data-testid="track-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="TRV-000001, PKT-000001, atau 08xxxx" onKeyDown={(e) => e.key === "Enter" && search()} />
          <Button data-testid="track-search" onClick={search} disabled={loading} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90"><Search className="mr-1 h-4 w-4" /> Cek</Button>
        </div>

        {checked && pkg && <div className="mt-8"><PackageCard p={pkg} /></div>}
        {checked && order && <div className="mt-8"><OrderCard o={order} withTimeline /></div>}
        {checked && list && (
          <div className="mt-8 space-y-4">
            <div className="text-sm text-muted-foreground">{list.length} order ditemukan</div>
            {list.map((o) => <OrderCard key={o.id} o={o} withTimeline />)}
            {list.length === 0 && <p className="border border-border bg-card p-6 text-center text-muted-foreground" data-testid="track-empty">Belum ada order untuk nomor ini.</p>}
          </div>
        )}
        {checked && !order && !list && !pkg && <p className="mt-6 border border-border bg-card p-6 text-center text-muted-foreground" data-testid="track-notfound">Data tidak ditemukan.</p>}
      </div>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
